<script setup lang="ts">
import { ref, watch } from 'vue'
import { journalsApi } from '../../api/journalsApi'
import { useErrorHandler } from '../../composables/useErrorHandler'
import { useFormCloseGuard } from '@/modules/journals/composables/useFormCloseGuard'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

interface Props {
  isOpen: boolean
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'created'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const defaultFormData = () => ({
  name: '',
  description: '',
  is_public: true,
})

const formData = ref(defaultFormData())
const initialFormSnapshot = ref('')
const isSubmitting = ref(false)
const formRef = ref()

const rules = {
  name: [(v: string) => !!v || 'Название обязательно'],
}

const getFormSnapshot = () => JSON.stringify(formData.value)

const hasUnsavedChanges = () => getFormSnapshot() !== initialFormSnapshot.value

const resetForm = () => {
  formData.value = defaultFormData()
  formRef.value?.resetValidation()
}

const forceClose = () => {
  emit('update:isOpen', false)
  resetForm()
}

const {
  showUnsavedChangesDialog,
  requestClose,
  handleCloseConfirm,
  onDialogDismissAttempt,
  onDialogUpdate,
} = useFormCloseGuard({
  hasUnsavedChanges,
  forceClose,
})

watch(
  () => props.isOpen,
  isOpen => {
    if (!isOpen) {
      return
    }

    resetForm()
    initialFormSnapshot.value = getFormSnapshot()
  },
)

const { handleError, showSuccess } = useErrorHandler()

const onSubmit = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) {
    return
  }

  isSubmitting.value = true
  try {
    const payload = {
      name: formData.value.name,
      description: formData.value.description,
      is_public: formData.value.is_public,
    }

    await journalsApi.createJournal(payload)
    showSuccess('Журнал успешно создан')
    emit('created')
    forceClose()
  } catch (error) {
    handleError(error, 'Ошибка при создании журнала')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="isOpen"
    max-width="600"
    persistent
    @update:model-value="onDialogUpdate"
    @click:outside="onDialogDismissAttempt"
    @keydown.esc.stop="onDialogDismissAttempt"
  >
    <VCard class="pa-6">
      <div class="d-flex mb-6">
        <div class="form_card d-flex align-center justify-space-between w-100">
          <div class="form_card__title">
            <span class="text-h4">Добавить журнал</span>
          </div>
          <CustomCloseButton :close-action="requestClose" />
        </div>
      </div>

      <div class="d-flex flex-column gap-2">
        <VForm
          ref="formRef"
          class="journal-form-fields"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="formData.name"
                variant="outlined"
                persistent-placeholder
                :rules="rules.name"
                required
              >
                <template #label>
                  <span>Название журнала<sup class="required-label">*</sup></span>
                </template>
              </VTextField>
            </VCol>

            <VCol cols="12">
              <VTextarea
                v-model="formData.description"
                variant="outlined"
                persistent-placeholder
                rows="3"
              >
                <template #label>
                  <span>Описание</span>
                </template>
              </VTextarea>
            </VCol>

            <VCol cols="12">
              <VSwitch
                v-model="formData.is_public"
                label="Публичный журнал"
                color="primary"
              />
            </VCol>
          </VRow>
        </VForm>

        <div class="d-flex justify-end gap-2">
          <VBtn
            variant="outlined"
            :disabled="isSubmitting"
            @click="requestClose"
          >
            Отмена
          </VBtn>
          <VBtn
            color="primary"
            :loading="isSubmitting"
            @click="onSubmit"
          >
            Создать журнал
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>

  <ConfirmDialog
    v-model:is-dialog-visible="showUnsavedChangesDialog"
    confirmation-question="Закрыть без сохранения?"
    confirmation-text="Данные не сохранятся"
    confirm-btn="Закрыть"
    reject-btn="Продолжить"
    @confirm="handleCloseConfirm"
  />
</template>

<style scoped lang="scss">
@use '@/modules/journals/styles/journalFormFields' as jf;

.journal-form-fields {
  @include jf.fields-height;
}

.form_card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.form_card__title {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Стили для статусов */
.status-badge.new {
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-badge.in_progress {
  background-color: #fff3e0;
  color: #f57c00;
}

.status-badge.completed {
  background-color: #e8f5e8;
  color: #388e3c;
}

.status-badge.not_finished {
  background-color: #f5f5f5;
  color: #616161;
}

.required-label {
  color: rgb(var(--v-theme-error));
}

/* Адаптивные стили */
.cursor-pointer {
  cursor: pointer;
}

.text-italic {
  font-style: italic;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gap-2 {
  gap: 8px;
}

.col-span-12 {
  grid-column: span 12;
}
</style>
